import React from "react";
import {
  LayoutGrid,
  MessagesSquare,
  FileSpreadsheet,
  FolderOpen,
  Eye,
  CalendarDays,
  MessageCircle,
  Rocket,
  History,
  Sparkles,
  ChevronRight,
  Home,
  Crown,
} from "lucide-react";
import { Avatar } from "../Avatar";
import { leadNegotiator } from "../../data/launchHubData";

const items = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "team-chat", label: "Team Chat", icon: MessagesSquare, badge: "3" },
  { id: "brochure", label: "Brochure Review", icon: FileSpreadsheet, badge: "v3" },
  { id: "documents", label: "Documents", icon: FolderOpen, badge: "3" },
  { id: "viewings", label: "Viewings", icon: Eye },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "feedback", label: "Feedback", icon: MessageCircle },
  { id: "readiness", label: "Launch Readiness", icon: Rocket, badge: "84%" },
  { id: "activity", label: "Activity Timeline", icon: History },
  { id: "ai", label: "AI Insights", icon: Sparkles, badge: "AI" },
];

export const LaunchHubSidebar = ({ activeSection, onJump }) => {
  return (
    <aside
      className="fixed left-0 top-0 h-screen z-40 glass-dark border-r border-white/[0.06] flex flex-col"
      style={{ width: 276 }}
      data-testid="launchhub-sidebar"
    >
      {/* Aurora */}
      <div className="pointer-events-none absolute -top-32 -left-24 w-72 h-72 cf-blob opacity-50" />
      <div className="pointer-events-none absolute bottom-40 -right-12 w-48 h-48 cf-blob opacity-30" />

      {/* Brand */}
      <div className="relative px-5 pt-7 pb-5 flex items-center gap-3">
        <a
          href="/dashboard"
          className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400/30 via-indigo-500/15 to-indigo-600/10 border border-indigo-400/30 flex items-center justify-center cf-glow shrink-0"
          data-testid="launchhub-brand-logo"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="12" r="5.25" stroke="#818CF8" strokeWidth="1.9" />
            <circle cx="15" cy="12" r="5.25" stroke="#818CF8" strokeWidth="1.9" />
          </svg>
        </a>
        <div className="flex flex-col leading-none min-w-0">
          <span className="font-display text-white text-[18px] font-bold tracking-tight">ChainFlow</span>
          <span className="text-[9.5px] uppercase tracking-[0.26em] text-indigo-400/85 mt-1.5 font-semibold">
            Operating System
          </span>
        </div>
      </div>

      <div className="cf-hairline-dark mx-5 mb-3" />

      {/* Hub title */}
      <div className="px-5 mb-3">
        <a
          href="/dashboard"
          className="flex items-center gap-1.5 text-[10.5px] text-slate-400 hover:text-indigo-300 transition-colors mb-2"
          data-testid="launchhub-back-link"
        >
          <Home size={11} /> Back to dashboard
        </a>
        <div
          className="rounded-xl px-3.5 py-3 border border-indigo-400/25 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(99, 102, 241,0.18) 0%, rgba(99,102,241,0.10) 60%, transparent 100%)",
          }}
          data-testid="launchhub-title-card"
        >
          <div className="absolute -top-8 -right-6 w-24 h-24 rounded-full bg-indigo-400/15 blur-2xl pointer-events-none" />
          <div className="flex items-center gap-1.5 text-[9.5px] text-indigo-300 uppercase tracking-[0.22em] font-semibold mb-1.5">
            <Rocket size={10} /> Active workspace
          </div>
          <p className="text-white text-[14px] font-bold tracking-tight leading-tight">
            Seller Launch Hub
          </p>
          <p className="text-[10.5px] text-slate-300 mt-1 truncate">12 Mersey Reach · Aigburth</p>
        </div>
      </div>

      {/* Nav sections */}
      <nav className="flex-1 px-3 overflow-y-auto cf-scroll-dark pb-4">
        <p className="px-3 mb-2 text-[9.5px] uppercase tracking-[0.26em] text-slate-500/85 font-semibold">
          Workspace
        </p>
        <ul className="space-y-0.5">
          {items.map((item) => {
            const Icon = item.icon;
            const active = activeSection === item.id;
            return (
              <li key={item.id}>
                <button
                  onClick={() => onJump(item.id)}
                  data-testid={`launchhub-nav-${item.id}`}
                  className={`cf-nav-item ${active ? "cf-nav-active" : ""} w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium ${
                    active
                      ? ""
                      : "text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent"
                  }`}
                >
                  <Icon size={17} strokeWidth={1.6} className="shrink-0 transition-colors" />
                  <span className="flex-1 truncate text-left">{item.label}</span>
                  {item.badge && (
                    <span
                      className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded-md border ${
                        item.badge === "AI"
                          ? "bg-indigo-500/20 text-indigo-300 border-indigo-400/30"
                          : "bg-white/[0.06] text-slate-300 border-white/[0.05]"
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Upgrade card */}
      <div className="px-4 pb-3">
        <div
          className="relative overflow-hidden rounded-xl p-4 border border-indigo-400/30"
          style={{
            background:
              "linear-gradient(140deg, rgba(99, 102, 241, 0.20) 0%, rgba(99, 102, 241, 0.10) 60%, transparent 100%)",
          }}
          data-testid="launchhub-upgrade-card"
        >
          <div className="absolute -top-10 -right-8 w-28 h-28 rounded-full bg-indigo-400/20 blur-2xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-1.5 text-[9.5px] text-indigo-300 uppercase tracking-[0.22em] font-semibold mb-1.5">
              <Crown size={10} className="fill-indigo-300/80" /> Plan · Pro
            </div>
            <p className="text-white text-[12.5px] leading-tight">
              Unlock <span className="font-semibold">Chain Intelligence</span> & multi-branch
              forecasting.
            </p>
            <button
              className="mt-3 w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-[11.5px] font-semibold transition-all"
              style={{
                background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
                boxShadow:
                  "0 6px 18px -4px rgba(99, 102, 241, 0.50), inset 0 1px 0 rgba(255,255,255,0.18)",
              }}
              data-testid="launchhub-upgrade-btn"
            >
              Upgrade Plan <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* User profile */}
      <div className="px-4 pb-4 pt-1 border-t border-white/[0.06]">
        <div
          className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-white/[0.04] transition-colors cursor-pointer"
          data-testid="launchhub-user-card"
        >
          <Avatar person={leadNegotiator} size={36} online />
          <div className="flex-1 min-w-0">
            <p className="text-white text-[12.5px] font-semibold truncate leading-tight">
              {leadNegotiator.name}
            </p>
            <p className="text-[10.5px] text-slate-400 truncate mt-0.5">{leadNegotiator.role}</p>
          </div>
          <ChevronRight size={14} className="text-slate-500 shrink-0" />
        </div>
      </div>
    </aside>
  );
};
